import { ArrowRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { BillingInterval } from "@/api/billing";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCan } from "@/hooks/use-permissions";
import { onPaymentRequired } from "@/lib/api-client";
import type { BillingDialogReason } from "./billing-events";
import { formatCredits } from "./format";
import { NotConfiguredNotice } from "./not-configured";
import { PackGrid } from "./pack-grid";
import { IntervalToggle, PlanGrid } from "./plan-grid";
import { useBillingActions, useBillingCatalog, useBillingSummary, useCredits } from "./use-billing";

type DialogState = { reason: BillingDialogReason; message: string } | null;

const COPY: Record<BillingDialogReason, { tag: string; title: string }> = {
	plan_limit: { tag: "402 · Plan limit", title: "Upgrade to keep going" },
	insufficient_credits: { tag: "402 · Insufficient credits", title: "Top up to keep generating" },
	upgrade: { tag: "Plans", title: "Choose a plan" },
	credits: { tag: "Credits", title: "Buy credits" },
};

/**
 * Mounted once in the app shell. Opens on any 402 (`plan_limit` → plans, `insufficient_credits`
 * → packs + plans) and on `openBillingDialog()`.
 */
export function BillingDialogHost() {
	const [state, setState] = useState<DialogState>(null);
	useEffect(
		() =>
			onPaymentRequired((error) => {
				const reason = (
					["plan_limit", "insufficient_credits", "upgrade", "credits"].includes(error.code)
						? error.code
						: "insufficient_credits"
				) as BillingDialogReason;
				setState({ reason, message: error.message });
			}),
		[],
	);
	return (
		<Dialog open={!!state} onOpenChange={(open) => !open && setState(null)}>
			<DialogContent className="top-[8%] max-h-[84dvh] overflow-y-auto sm:max-w-4xl">
				{state && <BillingDialogBody state={state} onClose={() => setState(null)} />}
			</DialogContent>
		</Dialog>
	);
}

function BillingDialogBody({
	state,
	onClose,
}: {
	state: NonNullable<DialogState>;
	onClose: () => void;
}) {
	const { data: summary } = useBillingSummary();
	const { data: catalog } = useBillingCatalog();
	const credits = useCredits();
	const [interval, setInterval] = useState<BillingInterval>(
		summary?.subscription?.interval ?? "month",
	);
	const actions = useBillingActions(summary);
	const canManage = useCan("billing.manage");
	const configured = summary?.configured ?? true;
	const showPacks = state.reason === "insufficient_credits" || state.reason === "credits";
	const copy = COPY[state.reason];
	const disabledReason = !configured
		? "Billing isn't configured yet"
		: !canManage
			? "Your role can't change billing"
			: undefined;
	const trialEligible =
		!summary?.subscription?.managedByStripe && !summary?.subscription?.trialEndsAt;

	return (
		<div className="flex flex-col gap-5">
			<DialogHeader>
				<span className="mono-label">{copy.tag}</span>
				<DialogTitle className="text-lg">{copy.title}</DialogTitle>
				<DialogDescription>
					{state.message ||
						(showPacks
							? "Credits pay for generations at model cost plus a transparent margin."
							: "Every plan includes monthly credits. Change or cancel anytime.")}
				</DialogDescription>
				{credits.total != null && (
					<p className="font-mono text-[12px] text-muted-foreground">
						Balance {formatCredits(credits.spendable ?? 0)} credits
						{summary?.plan ? ` · ${summary.plan.name} plan` : ""}
					</p>
				)}
			</DialogHeader>

			{!configured && <NotConfiguredNotice compact />}
			{configured && !canManage && (
				<p className="rounded-md border border-dashed px-3 py-2 text-[13px] text-muted-foreground">
					Ask someone who can manage billing to upgrade or buy credits.
				</p>
			)}

			{showPacks && (
				<section className="flex flex-col gap-2">
					<span className="mono-label">Credit packs</span>
					{catalog && summary ? (
						<PackGrid
							packs={catalog.packs}
							pendingPackId={actions.pending}
							disabled={!configured || !canManage}
							disabledReason={disabledReason}
							onBuy={actions.buyPack}
						/>
					) : (
						<Skeleton className="h-40 w-full" />
					)}
				</section>
			)}

			<section className="flex flex-col gap-2">
				<div className="flex items-center justify-between gap-2">
					<span className="mono-label">{showPacks ? "Or get more every month" : "Plans"}</span>
					<IntervalToggle value={interval} onChange={setInterval} />
				</div>
				{catalog && summary ? (
					<PlanGrid
						compact
						plans={catalog.plans}
						currentPlanId={summary?.plan?.id}
						currentInterval={summary?.subscription?.interval}
						interval={interval}
						pendingPlanId={actions.pending}
						disabled={!configured || !canManage}
						disabledReason={disabledReason}
						trialEligible={trialEligible}
						onChoose={(plan) => actions.choosePlan(plan, interval)}
					/>
				) : (
					<Skeleton className="h-64 w-full" />
				)}
			</section>

			<div className="flex justify-end">
				<Button asChild variant="ghost" size="sm" onClick={onClose}>
					<Link to="/settings/billing">
						Billing & usage <ArrowRightIcon />
					</Link>
				</Button>
			</div>
		</div>
	);
}
